<script setup lang="ts">
import { DEEPL_MODEL_TYPES } from '@sky-lingo-v2/shared';
import type { TranslationProvider, TranslationPublicConfig } from '@sky-lingo-v2/shared';
import { credentialHint, createProviderInputs, providerDetails } from './translationProviders';
import ProviderLogo from './ProviderLogo.vue';

defineProps<{
  translation: TranslationPublicConfig;
  inputs: ReturnType<typeof createProviderInputs>;
  tests: Record<TranslationProvider, { loading: boolean; ok: boolean | null; message: string }>;
  canWrite: boolean;
  saving: boolean;
}>();
const emit = defineEmits<{ save: []; test: [provider: TranslationProvider] }>();
const providers: TranslationProvider[] = ['google', 'deepl', 'ai', 'qwen', 'aai', 'hunyuan', 'index35b', 'bing', 'index2b'];

function deepLModeLabel(mode: typeof DEEPL_MODEL_TYPES[number]): string {
  return mode === 'latency_optimized' ? '优先响应速度'
    : mode === 'quality_optimized' ? '优先翻译质量' : '优先质量，可按语言回退';
}
</script>

<template>
  <section class="translationSettingsPanel" aria-label="翻译服务配置">
    <div class="translationSettingsIntro">
      <div>
        <h3>接口凭证</h3>
        <p>原有服务分别配置密钥；三个备用通道无需密钥，启用并授权后由用户手动切换。</p>
      </div>
      <button v-if="canWrite" type="button" class="translationSettingsSave" :disabled="saving" @click="emit('save')">
        {{ saving ? '正在保存…' : '保存配置' }}
      </button>
    </div>

    <div class="translationSettingsGrid">
      <article v-for="provider in providers" :key="provider" class="translationSettingsCard" :data-settings-provider="provider">
        <div class="translationSettingsCardHeader">
          <span class="translationSettingsIcon"><ProviderLogo :provider="provider" /></span>
          <strong>
            {{ providerDetails[provider].label }}
            <small v-if="providerDetails[provider].backup" class="translationSettingsBackup">备用</small>
            <b class="translationSettingsBadge" :class="translation.providers[provider].configured ? 'configured' : 'unconfigured'">
              {{ providerDetails[provider].backup ? '无需密钥' : translation.providers[provider].configured ? '已配置' : '未配置' }}
            </b>
          </strong>
          <label class="translationSettingsEnable">
            <input v-model="translation.providers[provider].enabled" type="checkbox" :disabled="!canWrite || saving" />
            <span aria-hidden="true"></span>
            <small>启用</small>
          </label>
        </div>

        <p v-if="providerDetails[provider].backup" class="translationSettingsBackupNote">默认停用。需启用并授予用户权限，再由用户手动选择；不会自动切换或兜底。</p>
        <div v-if="providerDetails[provider].credentials.length" class="translationSettingsFields">
          <label v-for="field in providerDetails[provider].credentials" :key="field.key" class="translationSettingsField">
            <span>{{ field.label }}<small v-if="credentialHint(translation.providers[provider], field.key)">{{ credentialHint(translation.providers[provider], field.key) }}</small></span>
            <input
              v-model="inputs[provider][field.key]"
              type="password"
              autocomplete="new-password"
              :placeholder="translation.providers[provider].configured ? '已配置，留空保留' : field.label"
              :disabled="!canWrite || saving || inputs[provider].clear[field.key]"
            />
          </label>
          <label v-if="provider === 'deepl'" class="translationSettingsField">
            <span>API 模型偏好</span>
            <select v-model="translation.providers.deepl.modelType" :disabled="!canWrite || saving">
              <option :value="undefined" disabled>沿用服务默认模式</option>
              <option v-for="mode in DEEPL_MODEL_TYPES" :key="mode" :value="mode">{{ deepLModeLabel(mode) }}</option>
            </select>
          </label>
        </div>

        <details v-if="providerDetails[provider].credentials.length || providerDetails[provider].metadata.length" class="translationSettingsAdvanced">
          <summary>高级设置<span v-if="providerDetails[provider].credentials.some(field => inputs[provider].clear[field.key])"> · 有凭据待清除</span></summary>
          <div v-if="providerDetails[provider].metadata.length" class="translationSettingsFields translationSettingsMetadata">
            <label v-for="field in providerDetails[provider].metadata" :key="field.key" class="translationSettingsField">
              <span>{{ field.label }}</span>
              <input v-if="field.numeric" v-model.number="translation.providers[provider].projectId" type="number" min="0" step="1" :placeholder="field.placeholder" :disabled="!canWrite || saving" />
              <input v-else v-model="translation.providers[provider][field.key]" type="text" :placeholder="field.placeholder" :disabled="!canWrite || saving" />
            </label>
          </div>
          <p>勾选后，保存配置时才会清除对应凭据；不勾选且输入留空会保留原值。</p>
          <div class="translationSettingsClearFields">
            <label v-for="field in providerDetails[provider].credentials" :key="field.key">
              <input v-model="inputs[provider].clear[field.key]" type="checkbox" :disabled="!canWrite || saving" />
              保存时清除 {{ field.label }}
            </label>
          </div>
        </details>

        <div class="translationSettingsFooter">
          <span v-if="tests[provider].message" class="translationSettingsTestResult" :class="tests[provider].ok === true ? 'success' : tests[provider].ok === false ? 'failure' : ''" aria-live="polite">{{ tests[provider].message }}</span>
          <span v-else>{{ providerDetails[provider].backup ? '先保存并启用，再检测真实接口；公共通道可能限流。' : '先保存配置，再检测真实接口；检测可能产生供应商费用。' }}</span>
          <button v-if="canWrite" type="button" class="translationSettingsTest" :disabled="saving || tests[provider].loading" @click="emit('test', provider)">
            {{ tests[provider].loading ? '检测中…' : '检测接口' }}
          </button>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.translationSettingsPanel { min-width: 0; }
.translationSettingsIntro { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
.translationSettingsIntro h3 { margin: 0; font-size: 14px; font-weight: 600; }
.translationSettingsIntro p { margin: 5px 0 0; color: var(--muted); font-size: 11px; line-height: 1.6; }
.translationSettingsSave, .translationSettingsTest { flex: 0 0 auto; min-height: 36px; padding: 7px 12px; border: 1px solid var(--line); border-radius: 7px; font-size: 12px; font-weight: 500; cursor: pointer; }
.translationSettingsSave { color: var(--bg); border-color: var(--accent); background: var(--accent); }
.translationSettingsTest { color: var(--text); background: var(--surface-soft); }
.translationSettingsSave:disabled, .translationSettingsTest:disabled { opacity: .55; cursor: wait; }
.translationSettingsSave:focus-visible, .translationSettingsTest:focus-visible, .translationSettingsAdvanced summary:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.translationSettingsGrid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.translationSettingsCard { display: flex; flex-direction: column; min-width: 0; padding: 16px; border: 1px solid var(--line); border-radius: 11px; background: var(--surface); }
.translationSettingsCardHeader { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.translationSettingsIcon { display: grid; place-items: center; width: 32px; height: 32px; padding: 6px; border: 1px solid var(--line); border-radius: 8px; background: var(--surface-raised); flex: 0 0 auto; }
.translationSettingsIcon :deep(.provider-logo) { width: 100%; height: 100%; }
.translationSettingsCardHeader > strong { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; flex: 1; gap: 7px; min-width: 0; color: var(--text); font-size: 13px; font-weight: 600; line-height: 1.5; }
.translationSettingsBadge { display: inline-flex; align-items: center; justify-content: center; padding: 5px 9px; border-radius: 6px; font-size: 10px; font-weight: 500; line-height: 1.5; white-space: nowrap; }
.translationSettingsBadge.configured { color: var(--accent); background: color-mix(in srgb, var(--accent) 9%, transparent); }
.translationSettingsBadge.unconfigured { color: var(--warn); background: color-mix(in srgb, var(--warn) 9%, transparent); }
.translationSettingsBackup { color: var(--muted); font-size: 11px; font-weight: 400; }
.translationSettingsBackupNote { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.translationSettingsEnable { display: flex; align-items: center; gap: 5px; flex: 0 0 auto; cursor: pointer; }
.translationSettingsEnable input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.translationSettingsEnable > span { display: block; width: 28px; height: 16px; border: 1px solid var(--line); border-radius: 20px; background: var(--surface-soft); }
.translationSettingsEnable > span::after { content: ''; display: block; width: 10px; height: 10px; margin: 2px; border-radius: 50%; background: var(--muted); transition: transform .15s; }
.translationSettingsEnable input:checked + span { border-color: var(--accent); background: var(--accent); }
.translationSettingsEnable input:checked + span::after { transform: translateX(12px); background: var(--surface); }
.translationSettingsEnable input:focus-visible + span { outline: 2px solid var(--accent); outline-offset: 2px; }
.translationSettingsEnable input:disabled ~ span, .translationSettingsEnable input:disabled ~ small { opacity: .5; cursor: not-allowed; }
.translationSettingsEnable small { color: var(--muted); font-size: 10px; }
.translationSettingsFields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.translationSettingsFields > label:only-child { grid-column: 1 / -1; }
.translationSettingsField { display: flex; flex-direction: column; gap: 6px; min-width: 0; color: var(--muted); font-size: 11px; line-height: 1.5; }
.translationSettingsField > span { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 3px 6px; }
.translationSettingsField small { color: var(--muted); font-size: 10px; overflow-wrap: anywhere; }
.translationSettingsField input, .translationSettingsField select { box-sizing: border-box; width: 100%; min-height: 36px; padding: 8px 10px; color: var(--text); background: var(--surface-soft); border: 1px solid var(--line); border-radius: 7px; font-size: 12px; line-height: 1.5; }
.translationSettingsField input:focus, .translationSettingsField select:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.translationSettingsField input:disabled, .translationSettingsField select:disabled { opacity: .65; }
.translationSettingsAdvanced { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line); }
.translationSettingsAdvanced summary { color: var(--muted); font-size: 11px; line-height: 1.6; cursor: pointer; }
.translationSettingsAdvanced summary > span { color: var(--warn); }
.translationSettingsMetadata { margin-top: 10px; }
.translationSettingsAdvanced p { margin: 9px 0 6px; color: var(--muted); font-size: 10px; line-height: 1.6; }
.translationSettingsClearFields { display: flex; flex-wrap: wrap; gap: 6px 12px; }
.translationSettingsClearFields label { display: inline-flex; align-items: center; gap: 5px; color: var(--muted); font-size: 11px; }
.translationSettingsClearFields input { width: 14px; height: 14px; min-height: 0; margin: 0; padding: 0; accent-color: var(--accent); }
.translationSettingsFooter { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 14px; padding-top: 10px; border-top: 1px solid var(--line); }
.translationSettingsFooter > span { min-width: 0; color: var(--muted); font-size: 10px; line-height: 1.6; overflow-wrap: anywhere; }
.translationSettingsTestResult.success { color: var(--accent); }
.translationSettingsTestResult.failure { color: var(--warn); }
@media (min-width: 1540px) { .translationSettingsGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (min-width: 881px) and (max-width: 1539px) { .translationSettingsCard:last-child:nth-child(odd) { grid-column: 1 / -1; } }
@media (max-width: 880px) { .translationSettingsGrid { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 640px) {
  .translationSettingsIntro { flex-wrap: wrap; }
  .translationSettingsFields { grid-template-columns: minmax(0, 1fr); }
  .translationSettingsCardHeader > strong { align-items: flex-start; }
  .translationSettingsField input, .translationSettingsField select { min-height: 40px; }
  .translationSettingsFooter { flex-wrap: wrap; }
  .translationSettingsTest { margin-left: auto; }
}
</style>
